/**
 * @file DeleteAccountPublicPage.tsx
 * @brief Public "Delete your VERBLIZR account" pages, no sign-in (Google
 *        Play, BACKEND B7.3): `/delete-account` and `/delete-account/confirm`.
 *
 * @details
 * 1. `/delete-account`: email → always "Check your email" (the server says
 *    the same whether or not the account exists).
 * 2. The email's button opens the confirm page with a one-time token
 *    (the backend links to the website's copy, X1; this one works the same).
 *    It shows what confirming does (personal: deleted within 30 days;
 *    company: a request to the admin) and a red Confirm.
 * Every sentence shown is the server's `message` as is. Linked from the
 * sign-in footer (AuthLayout). Works signed in or out.
 *
 * Needs the panel's origin in the backend's PUBLIC_DELETION_ORIGINS (Cloud
 * Run env, DES) or the browser blocks the call.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, LoaderCircle, Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";
import { ServerError } from "@/features/auth/register/registerUi";
import { env } from "@/shared/config/env";
import { cn } from "@/shared/theme/cn";
import { createPublicDeletionApi, isLikelyEmail, parseConfirmInfo, type ConfirmInfo } from "./publicDeletion";

type Api = ReturnType<typeof createPublicDeletionApi>;

const primaryBtn = "h-12 w-full bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90";

function useApi(api?: Api): Api {
  return useMemo(() => api ?? createPublicDeletionApi(env.apiBaseUrl), [api]);
}

function Done({ title, message }: { title: string; message: string }) {
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <Check className="h-24 w-24 text-emerald-500 duration-500 animate-in zoom-in-50 fade-in-0" strokeWidth={2.5} aria-hidden />
      <h2 className="mt-4 text-[28px] font-bold leading-[34px] tracking-tight text-foreground">{title}</h2>
      <p className="mt-2 text-[15px] leading-5 text-muted-foreground">{message}</p>
      <Link to="/login" className="mt-8 text-sm font-medium text-brand hover:underline">
        Back to Sign in
      </Link>
    </div>
  );
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-8">
      <h1 className="text-[28px] font-bold leading-[34px] tracking-tight text-foreground">{title}</h1>
      <p className="mt-2 text-[15px] leading-5 text-muted-foreground">{subtitle}</p>
    </div>
  );
}

/** `/delete-account`: ask for a confirm link. */
export function DeleteAccountPublicPage({ api: injected }: { api?: Api }) {
  const api = useApi(injected);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isLikelyEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await api.request(email);
    setBusy(false);
    if (r.ok) setSent(r.message);
    else setError(r.message);
  };

  if (sent) return <Done title="Check your email" message={sent} />;

  return (
    <div>
      <Header
        title="Delete your account"
        subtitle="Enter the email you use for VERBLIZR. We'll send you a link to confirm. You don't need to sign in."
      />
      <form onSubmit={submit} className="space-y-3.5" noValidate>
        <FormFieldGroup label="Email Address" htmlFor="delete-email" icon={<Mail />}>
          <Input
            id="delete-email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormFieldGroup>
        <ServerError message={error} />
        <Button type="submit" className={cn(primaryBtn, "mt-4")} size="lg" disabled={busy}>
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Send confirm link"}
        </Button>
      </form>
      <ul className="mt-8 space-y-2 text-xs leading-5 text-muted-foreground">
        <li>Personal account: your account, meetings, notes and recordings are erased within 30 days and any plan stops.</li>
        <li>Company account: your request goes to your company administrator and VERBLIZR support; it is completed within 30 days.</li>
        <li>Invoices are kept for 6 years, as the law requires.</li>
      </ul>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-brand hover:underline">
          Back to Sign in
        </Link>
      </p>
    </div>
  );
}

/** `/delete-account/confirm?token=…`: the emailed link. */
export function DeleteAccountConfirmPage({ api: injected }: { api?: Api }) {
  const api = useApi(injected);
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [info, setInfo] = useState<ConfirmInfo | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setInvalid("This link has expired or has already been used. Please ask for a new one.");
      return;
    }
    let gone = false;
    void api.lookup(token).then((r) => {
      if (gone) return;
      const parsed = r.ok ? parseConfirmInfo(r.data) : null;
      if (parsed) setInfo(parsed);
      else setInvalid(r.message);
    });
    return () => {
      gone = true;
    };
  }, [api, token]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    const r = await api.confirm(token);
    setBusy(false);
    if (r.ok) {
      setDone({ title: r.code === "DELETION_REQUESTED" ? "Request sent" : "Deletion started", message: r.message });
    } else if (r.code === "LINK_INVALID") {
      setInvalid(r.message);
    } else {
      setError(r.message);
    }
  };

  if (done) return <Done title={done.title} message={done.message} />;

  if (invalid) {
    return (
      <div>
        <Header title="This link doesn't work" subtitle={invalid} />
        <Button asChild className={primaryBtn} size="lg">
          <Link to="/delete-account">Ask for a new link</Link>
        </Button>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <LoaderCircle className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <Header
        title={info.accountType === "company" ? "Request account deletion" : "Delete your account"}
        subtitle={info.emailMasked ? `For ${info.emailMasked}` : ""}
      />
      <p className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm leading-5">{info.whatHappens}</p>
      <div className="mt-4">
        <ServerError message={error} />
      </div>
      <Button type="button" variant="destructive" className="mt-4 h-12 w-full gap-2 text-[15px] font-semibold" size="lg" disabled={busy} onClick={() => void confirm()}>
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        {info.accountType === "company" ? "Confirm request" : "Confirm deletion"}
      </Button>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-brand hover:underline">
          Cancel
        </Link>
      </p>
    </div>
  );
}
